import React from 'react';
import { Table } from 'antd';
import { formatDateHandler } from 'utils/common';
import TransferDocumentActions from './TransferDocumentActions';

/**
 * The expanded row for a stock transfer — and the *only* full view of one.
 *
 * There is no transfer detail page: the list serializer already returns
 * everything a transfer has (lines, notes, PO number/date/document), and the
 * backend's detail endpoint has never been called from the front end. So
 * rather than route to a page that would refetch data the list already holds,
 * this panel is the detail surface, and it has to carry every field the row
 * cannot: the notes (previously not rendered anywhere in the app after the
 * create modal closed) and the PO reference with its document actions.
 *
 * Layout: a header strip for the two free-text facts, then the line grid —
 * the same order of information as `PODetail`, which is the module's
 * established shape for "meta, then documents, then lines".
 */

/**
 * Normalise a transfer row into line objects.
 *
 * Rows created before multi-line transfers landed have no `lines[]`; their
 * single item lives in flat `uniform_item_name` / `size_code` / `quantity`
 * fields on the transfer itself. Both shapes are still served by the list
 * endpoint, so both have to render through the same columns. The synthesised
 * legacy row gets an id derived from the transfer so `rowKey` stays unique
 * across a page of mixed-vintage rows.
 */
export const getTransferLines = (record) => {
  if (record && record.lines && record.lines.length > 0) return record.lines;
  if (record && record.uniform_item_name) {
    return [{
      id: `legacy-${record.id}`,
      uniform_item_name: record.uniform_item_name,
      size_code: record.size_code || null,
      size_name: null,
      quantity: record.quantity,
    }];
  }
  return [];
};

/** True when the panel has anything at all to show for this transfer. */
export const hasTransferDetail = (record) => Boolean(record && (
  getTransferLines(record).length > 0
  // `.trim()`, because the API returns whitespace-only notes as a non-empty
  // string. Untrimmed, a note of "   " counts as detail here, so the row
  // advertises an expander that opens onto "No notes recorded." — and the same
  // trim below and on the list's glyph keeps all three checks agreeing.
  || record.notes?.trim()
  || record.po_number
  || record.po_date
  || record.has_po_document
));

const tabularNums = { fontVariantNumeric: 'tabular-nums' };

// Module scope, not per render: the columns close over nothing, so a stable
// identity lets antd skip re-deriving its internal column state.
const lineColumns = [
  {
    title: 'Item',
    dataIndex: 'uniform_item_name',
    render: (value) => value || <span className="text-muted">&mdash;</span>,
  },
  {
    title: 'Size',
    dataIndex: 'size_code',
    width: 190,
    // Code first because that is what the warehouse picks by; the human name
    // is the gloss, so it is muted and second — matching the PO line grid.
    render: (_, line) => (line.size_code ? (
      <span>
        <span style={{ fontWeight: 600 }}>{line.size_code}</span>
        {line.size_name ? <span className="text-muted ms-2">{line.size_name}</span> : null}
      </span>
    ) : <span className="text-muted">&mdash;</span>),
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    width: 120,
    align: 'right',
    render: (value) => <span style={tabularNums}>{value ?? 0}</span>,
  },
];

// `.detail-shell` is not available here, so the panel builds its own surface
// from the committed brand tokens. Inside `.list-shell` antd's own table
// chrome is already flattened, so the nested grid sits flush in this box.
const panelStyle = {
  border: '1px solid var(--brand-border)',
  borderRadius: 12,
  overflow: 'hidden',
  background: 'var(--brand-surface)',
};

const headStyle = {
  padding: '12px 16px',
  background: 'var(--brand-surface-muted)',
  borderBottom: '1px solid #F1F2F4',
};

const bodyStyle = { padding: '12px 16px 0' };

const footStyle = {
  padding: '10px 16px',
  borderTop: '1px solid #F1F2F4',
  fontSize: 12,
  color: 'var(--brand-text-muted)',
};

// Reuses the table header's type spec exactly (11 / 600 / 0.04em / uppercase)
// so the page has one label style rather than two.
const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--brand-text-muted)',
  marginBottom: 4,
};

const valueStyle = { fontSize: 13, color: 'var(--brand-text)', lineHeight: 1.5 };

const PanelField = ({ label, children }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={valueStyle}>{children}</div>
  </div>
);

function TransferExpandedPanel({ record, openingDoc, onOpenDocument }) {
  const lines = getTransferLines(record);
  const notes = record.notes?.trim();
  const totalQuantity = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
  const hasPoReference = Boolean(record.po_number || record.po_date || record.has_po_document);

  return (
    <div style={panelStyle}>
      <div style={headStyle}>
        <div className="row g-3">
          <div className="col-md-7">
            <PanelField label="Notes">
              {notes
                // pre-wrap, because notes are typed in a textarea and their
                // line breaks are usually the only structure they have.
                ? <span style={{ whiteSpace: 'pre-wrap' }}>{notes}</span>
                : <span className="text-muted">No notes recorded.</span>}
            </PanelField>
          </div>
          <div className="col-md-5">
            <PanelField label="PO Reference">
              {hasPoReference ? (
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span>{record.po_number || <span className="text-muted">No PO number</span>}</span>
                  {record.po_date ? (
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      {formatDateHandler(record.po_date)}
                    </span>
                  ) : null}
                  {record.has_po_document ? (
                    <TransferDocumentActions
                      record={record}
                      openingDoc={openingDoc}
                      onOpen={onOpenDocument}
                    />
                  ) : (
                    <span className="text-muted" style={{ fontSize: 12 }}>No document attached</span>
                  )}
                </div>
              ) : <span className="text-muted">No PO reference recorded.</span>}
            </PanelField>
          </div>
        </div>
      </div>

      <div style={bodyStyle}>
        <div style={labelStyle}>Items</div>
        <Table
          size="small"
          tableLayout="fixed"
          pagination={false}
          columns={lineColumns}
          dataSource={lines}
          rowKey={(line) => line.id}
          locale={{ emptyText: 'No line items recorded for this transfer.' }}
        />
      </div>

      {lines.length > 0 ? (
        <div style={footStyle} className="d-flex justify-content-between align-items-center">
          <span>{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
          <span>
            Total quantity{' '}
            <strong style={{ ...tabularNums, color: 'var(--brand-text)' }}>{totalQuantity}</strong>
          </span>
        </div>
      ) : null}
    </div>
  );
}

// Memoised because antd re-invokes `expandedRowRender` for every open row on
// every parent render. The caller narrows `openingDoc` to this record before
// passing it, so a document opening on one row does not re-render the nested
// grid of every other open row.
export default React.memo(TransferExpandedPanel);
